import React from 'react';
import classNames from 'classnames';
// import { injectIntl, intlShape } from 'react-intl';

import defs from 'components/shared/spriteDefs.jsx';

/**
 * Render an SVG icon from the sprite sheet.
 *
 * Icon source files are in `app/svgs/`.
 *
 * UI group: symbol value is the icon's file name (minus .svg)
 * BRAND group: symbol value is the icon's file name (minus .svg)
 * GRAPHICS group: symbol value is the the file name _before_ `-color.svg`. Specify the background color you want as a
 * prop, or leave empty for red (the default)
 *
 * Examples:
 *
 * <Sprite group="ui" symbol="email" />
 * <Sprite group="ui" symbol="error" />
 * <Sprite group="brand" symbol="logo-pc-optimum" />
 * <Sprite group="graphics" symbol="calculator" color="white" />
 * <Sprite group="graphics" symbol="optimum-card" color="red" background="pink" />
 *
 * Note: we set `focusable="false"` in the SVG because IE/Edge
 * by default will put SVGs in the tabbing order unless we do this
 * (it looks like a bug). Source: https://github.com/angular/material/issues/7250
 */
const Sprite = ({
	group,
	symbol,
	background,
	className,
	color = 'red',
	focusable = false,
	ariaHidden = true,
	role = 'presentation',
	// intl,
	title,
	...rest
}) => {
	const path = '/static/images/sprite.svg';

	if (!group || !symbol) {
		return null;
	}

	// TODO: Add i18n support; <IntlProvider> needs to exist in the component ancestry.
	// const formattedTitle = intl.formatMessage({ id: title || `sprite.${group}.${symbol}.title` });

	if (group === 'graphics' && background) {
		const wrapperClasses = classNames(
			'sprite--has-background',
			`sprite--background-${background}`,
			className
		);
		const symbolClasses = classNames(
			'sprite',
			'sprite__symbol',
			`sprite__symbol--${symbol}`
		);

		/*
		 * Note the {' '} markup around the <use> tag.  This fixes an issue with Safari
		 * 10 where SVGs reset the tabbing of the page to the beginning of the document.
		 * Please do not remove.
		 *
		 * Source: https://bugs.webkit.org/show_bug.cgi?id=162866
		 */
		return (
			<div className={wrapperClasses} {...rest}>
				<svg
					className={symbolClasses}
					focusable={focusable}
					aria-hidden={ariaHidden}
					role={role}
				>
					<title>{/* formattedTitle */}</title>
					{' '}<use xlinkHref={`${path}#${group}-${symbol}-${color}`} />{' '}
				</svg>
			</div>
		);
	}

	let fragment;
	let defaultSize;
	if (group === 'graphics') {
		fragment = `${group}-${symbol}-${color}`;
		defaultSize = '52px';
	} else if (group === 'ui') {
		fragment = `${group}-${symbol}`;
		defaultSize = '24px';
	} else if (group === 'brand') {
		fragment = `${group}-${symbol}`;
	}

	const symbolClasses = classNames(
		'sprite',
		className
	);

	/*
	 * Note the {' '} markup around the <use> tag.  This fixes an issue with Safari
	 * 10 where SVGs reset the tabbing of the page to the beginning of the document.
	 * Please do not remove.
	 *
	 * Source: https://bugs.webkit.org/show_bug.cgi?id=162866
	 */
	return (
		<svg
			width={defaultSize}
			height={defaultSize}
			aria-hidden={ariaHidden}
			focusable={focusable}
			role={role}
			className={symbolClasses}
			{...rest}
		>
			<title>{/* formattedTitle */}</title>
			{defs[group][symbol]}
			{' '}<use xlinkHref={`${path}#${fragment}`} />{' '}
		</svg>
	);
};
Sprite.propTypes = {
	symbol: React.PropTypes.string.isRequired,
	group: React.PropTypes.oneOf([
		'graphics',
		'ui',
		'brand',
	]).isRequired,
	color: React.PropTypes.string,
	background: React.PropTypes.string,
	className: React.PropTypes.string,
	focusable: React.PropTypes.bool,
	ariaHidden: React.PropTypes.bool,
	role: React.PropTypes.string,
	// intl: intlShape,
	title: React.PropTypes.string,
};

export default Sprite;



/** WEBPACK FOOTER **
 ** ./src/app/components/shared/sprite.jsx
 **/